import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import CartProgressBar from '../components/CartProgressBar';

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, cartTotal } = useCart();
  const { formatPrice } = useCurrency();

  if (cart.length === 0) {
    return (
      <div data-testid="cart-page-empty" className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h2 className="text-2xl text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Your Cart is Empty</h2>
          <p className="text-white/40 text-sm mb-6 font-sans">Discover our luxury fragrance collection</p>
          <Link to="/shop" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors" data-testid="continue-shopping-btn">
            Shop Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-white mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Shopping Cart
        </h1>

        {/* Cart Items */}
        <div className="space-y-4 mb-8" data-testid="cart-items">
          {cart.map(item => (
            <div key={item.cartKey} className="flex gap-4 bg-[#141414] border border-white/5 p-4" data-testid={`cart-item-${item.cartKey}`}>
              <div className="w-20 h-24 sm:w-24 sm:h-28 bg-[#1A1A1A] flex-shrink-0 overflow-hidden">
                <img src={item.image_url || 'https://images.pexels.com/photos/965990/pexels-photo-965990.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'} alt={item.product_name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-white text-base font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.product_name}</h3>
                    {item.variant ? (
                      <p className="text-white/40 text-xs font-sans mt-1">{item.variant.size} &middot; {item.variant.type}</p>
                    ) : (
                      <p className="text-white/40 text-xs font-sans mt-1">EDP &middot; 30% Oil &middot; Extrait-Level Performance</p>
                    )}
                  </div>
                  <button onClick={() => removeFromCart(item.cartKey)} className="text-white/30 hover:text-red-400 transition-colors" data-testid={`remove-item-${item.cartKey}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-white/20">
                    <button onClick={() => updateQuantity(item.cartKey, item.quantity - 1)} className="p-1.5 text-white/60 hover:text-white" data-testid={`cart-qty-decrease-${item.cartKey}`}>
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center text-white text-sm font-sans">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.cartKey, item.quantity + 1)} className="p-1.5 text-white/60 hover:text-white" data-testid={`cart-qty-increase-${item.cartKey}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-[#D4AF37] font-sans font-medium text-sm">{formatPrice(item.price * item.quantity)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="border-t border-white/10 pt-6 space-y-4">
          {/* #13 — Free shipping progress */}
          <CartProgressBar subtotal={cartTotal} />

          <div className="flex justify-between items-center mb-6">
            <span className="text-white/60 text-sm font-sans uppercase tracking-wider">Subtotal</span>
            <span className="text-white text-xl font-sans font-medium" data-testid="cart-total">{formatPrice(cartTotal)}</span>
          </div>
          <Link
            to="/checkout"
            className="block w-full text-center bg-[#D4AF37] text-black py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors"
            data-testid="proceed-to-checkout-btn"
          >
            Proceed to Checkout
          </Link>
          <Link to="/shop" className="block text-center text-white/40 hover:text-white text-sm mt-4 transition-colors" data-testid="continue-shopping-link">
            Continue Shopping
          </Link>
        </div>
      </div>

      {/* Mobile sticky checkout */}
      <div className="sticky-bottom md:hidden">
        <Link
          to="/checkout"
          className="block w-full text-center bg-[#D4AF37] text-black py-4 text-sm uppercase tracking-[0.15em] font-medium"
          data-testid="mobile-checkout-btn"
        >
          Checkout &middot; {formatPrice(cartTotal)}
        </Link>
      </div>
    </div>
  );
}
